import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import pluralize from 'pluralize';
import pMap from 'p-map-browser';

import { getAlbumCustomPrintName } from '../../../../../../../../../../util/custom-prints';
import AccountCard from '../../../../../../../../../../services/cards/components/AccountCard';
import useAccountCardConfigure from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/useAccountCardConfigure';
import {
  accountCardConfigurationValidate,
  getBackgroundAccountCardElement,
  getDefaultCardSideIds,
} from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/helpers';
import Spinner from '../../../../../../../../../../components/global/Spinner';
import Button from '../../../../../../../../../../components/global/Button';
import {
  getCardPrintProductConfiguration,
  getCardPrintProductSize,
  getCardTargetAccountCardConfiguration,
  getTextElementPresetData,
} from './helpers';
import DesignEditorPreviewContent from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane/DesignEditorPreviewContent';
import DesignEditorPreviewPane from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import DesignEditor from '../../../../../../../../../../components/global/DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderCenterSlot,
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import Angle from '../../../../../../../../../../components/global/Angle';
import ShoppingBasketIcon from '../../../../../../../../../../components/icons/ShoppingBasketIcon';
import AccountCardEmptyTextFormModal, {
  ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL,
} from './components/AccountCardEmptyTextFormModal';
import { shouldAlertEmptyText } from './components/AccountCardEmptyTextAlertModal/helpers';
import useOpenModal from '../../../../../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../../../../../hooks/useCloseModal';
import {
  getAccountCardElementsConfiguration,
  getCardTemplateSizing,
  isCardElementEditable,
} from '../../../../../../../../../../services/cards/components/AccountCard/helpers';
import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  ORIENTATION,
} from '../../../../../../../../../../services/cards/constants';
import { breakpointDown } from '../../../../../../../../../../services/responsive/responsive';
import {
  isMultiIdentityPackageMembership,
  isPictureDayMembership,
} from '../../../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import useTargetAccountCardInputFetch from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/useTargetAccountCardInputFetch';
import { TEXT_PRESETS } from '../../../../../../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import useIdentityJerseyColors from '../../../../../hooks/useIdentityJerseyColors';
import { getAutoFitPositionPhotoCropbox } from '../../../../../../../../../../services/cards/components/CardElementPhoto/helpers';
import { getMaxThumb } from '../../../../../../../../../../util/photo';
import { getCardPhotoElementsWithConfigurationFromAccountCard } from '../../../../../../../../../../services/prints/components/PrintProductGroupConfigurator/helpers';

const propTypes = {
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    custom_print: PropTypes.shape({
      id: PropTypes.string.isRequired,
      card: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  altClassName: PropTypes.string,
  onFinished: PropTypes.func.isRequired,
  deleteAccountCardOnDismiss: PropTypes.bool,
  buttonText: PropTypes.node,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  isAllPhotos: PropTypes.bool,
  forceEntitlement: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  title: PropTypes.string,
  inlineButton: PropTypes.bool,
};

const PRESET_FILL_STATUS = {
  LOADING: 'LOADING',
  FILLING: 'FILLING',
  FILLED: 'FILLED',
};

const AccountCardAlbumCustomPrintConfigurator = ({
  className,
  altClassName,
  onClose,
  onDismiss,
  albumCustomPrint,
  albumMembershipGroup,
  defaultAccountCard,
  onFinished,
  deleteAccountCardOnDismiss,
  buttonText = (
    <>
      <ShoppingBasketIcon
        color="#0c84fe"
        className="mr-1 flex-none"
        size={16}
      />
      Add to cart
    </>
  ),
  identity,
  isAllPhotos = false,
  forceEntitlement = false,
  watermarkSettings,
  photo,
  title,
  inlineButton,
}) => {
  const classes = classNames({
    [className]: !!className,
    [altClassName]: !!altClassName,
    'modal-contained-wrap': true,
  });
  const [presetFillStatus, setPresetFillStatus] = useState(
    PRESET_FILL_STATUS.LOADING,
  );
  const [emptyTextFormSeen, setEmptyTextFormSeen] = useState(false);
  const [
    accountCardElementReferences,
    setAccountCardElementReferences,
  ] = useState({});
  const name = getAlbumCustomPrintName(albumCustomPrint);
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const { card } = albumCustomPrint.custom_print;
  const printProductConfig = useMemo(
    () => getCardPrintProductConfiguration(card),
    [card],
  );
  const printProductSize = useMemo(() => getCardPrintProductSize(card), [card]);
  const linkImageElements = ['ORB_8_Trader_Cards_L'].includes(
    printProductSize?.sku,
  );

  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const {
    accountCard,
    onCardChange,
    onElementConfigurationChange,
    onAccountCardDelete,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId: defaultAccountCard?.id,
    presetCardElementConfigurationInputs: [],
    linkTextElements: true,
    linkImageElements,
  });
  const { teamName, levelName } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId: albumMembershipGroup?.is_album
      ? albumMembershipGroup?.album?.root_album_folder?.id
      : albumMembershipGroup?.id,
  });
  const targetAccountCardConfig = useMemo(
    () => getCardTargetAccountCardConfiguration(card),
    [card],
  );
  const {
    loading: loadingTargetAccountCardProduct,
    getTargetAccountCardInput,
  } = useTargetAccountCardInputFetch({
    printProductId: printProductConfig?.print_product_id,
  });

  useEffect(() => {
    // Create the AccountCard if we're starting from scratch.
    if (!defaultAccountCard && !loadingTargetAccountCardProduct) {
      onCardChange(
        {
          card_id: card.id,
          album_custom_print_id: albumCustomPrint?.id,
          print_product_configuration: getCardPrintProductConfiguration(card),
          ...getDefaultCardSideIds(card),
        },
        true,
        getTargetAccountCardInput(targetAccountCardConfig),
      );
    }
  }, [loadingTargetAccountCardProduct]);

  const editablePhotoElements = useMemo(() => {
    const elements = getAccountCardElementsConfiguration(
      accountCard,
      cardElementLoading,
      linkImageElements,
    );

    return (elements ?? []).filter(
      element =>
        element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        element.side === CARD_SIDE.FRONT &&
        isCardElementEditable(element),
    );
  }, [accountCard, cardElementLoading]);

  useEffect(() => {
    const onFillEditableElements = async editableTextElements => {
      setPresetFillStatus(PRESET_FILL_STATUS.FILLING);

      if (editablePhotoElements?.length === 1 && photo) {
        const { cardElement, cardElementConfiguration } =
          editablePhotoElements[0] ?? {};

        // We don't want to replace the cropbox if there is an existing configuration.
        if (!cardElementConfiguration?.photo?.id) {
          const {
            width: cardWidth,
            height: cardHeight,
          } = getCardTemplateSizing(
            card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
              .card_template,
          );
          const thumb = getMaxThumb(photo?.thumbnail_urls);
          const { width: photoWidth, height: photoHeight } = thumb ?? {};
          const photoIsLandscape = photoWidth >= photoHeight;
          const parsedFrame = cardElement?.frame
            ? {
                width:
                  Math.round(cardElement?.frame?.width * cardWidth * 1e2) / 1e2,
                height:
                  Math.round(cardElement?.frame?.height * cardHeight * 1e2) /
                  1e2,
              }
            : undefined;

          const frameOrientation =
            parsedFrame?.height > parsedFrame?.width
              ? ORIENTATION.PORTRAIT
              : ORIENTATION.LANDSCAPE;

          const orientationsMatch =
            (frameOrientation === ORIENTATION.LANDSCAPE && photoIsLandscape) ||
            (frameOrientation === ORIENTATION.PORTRAIT && !photoIsLandscape);

          await onElementConfigurationChange(editablePhotoElements[0], {
            photo_id: photo.id,
            is_editable: true,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb,
              faceboxes: (photo?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
              autoFitToFace: !orientationsMatch && photo?.faces?.total === 1,
            }),
          });
        }
      }

      await pMap(editableTextElements, async element => {
        const { cardElementConfiguration } = element ?? {};
        const presetKey = Object.keys(TEXT_PRESETS).find(
          k => TEXT_PRESETS[k] === cardElementConfiguration?.help_placeholder,
        );
        // We only want to automatically update values where a preset is established.
        // Therefore, we ensure a matching key exists on our array.
        if (!cardElementConfiguration?.text && presetKey) {
          const text = getTextElementPresetData({
            presetLabel: TEXT_PRESETS[presetKey],
            identity,
            albumMembershipGroup,
            teamName,
            levelName,
          });
          if (text) {
            await onElementConfigurationChange(element, { text });
          } else {
            // Pass dumb text here. The opacity will hide it, but the dumb content
            // will make the system assume that the entity is "filled" for empty text
            // slot purposes.
            await onElementConfigurationChange(element, {
              text: 'NULL',
              opacity: 0,
              is_editable: false,
            });
          }
        }
      });

      setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
    };

    if (
      presetFillStatus === PRESET_FILL_STATUS.LOADING &&
      accountCard &&
      !cardElementLoading
    ) {
      const editableTextElements = getAccountCardElementsConfiguration(
        accountCard,
        cardElementLoading,
      ).filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );

      if (
        editableTextElements.length > 0 ||
        (editablePhotoElements?.length === 1 && photo)
      ) {
        onFillEditableElements(editableTextElements);
      } else {
        setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
      }
    }
  }, [accountCard, cardElementLoading]);

  useEffect(() => {
    if (
      accountCard &&
      !emptyTextFormSeen &&
      presetFillStatus === PRESET_FILL_STATUS.FILLED &&
      shouldAlertEmptyText({ accountCard })
    ) {
      openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
        accountCard,
        onElementConfigurationChange,
        onComplete: () => {},
        canGoBack: false,
      });
      setEmptyTextFormSeen(true);
    }
  }, [accountCard, presetFillStatus, emptyTextFormSeen]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  const hasPhotoElementsMissingConfiguration = useMemo(() => {
    return !!editablePhotoElements.find(
      element =>
        !element?.cardElementConfiguration?.photo?.id &&
        !element?.cardElementConfiguration?.asset_image?.id,
    );
  }, [accountCard, cardElementLoading]);

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard,
  });

  const photoElements = getCardPhotoElementsWithConfigurationFromAccountCard({
    accountCard,
  }).filter(
    elem =>
      elem?.cardElement?.id !== backgroundElement?.cardElement?.id &&
      (!elem.cardElementConfiguration ||
        elem.cardElementConfiguration?.is_editable),
  );

  const [mainPhotoElement] = photoElements ?? [];

  const photoElementReference =
    accountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  const accountCardMainPhotoActionClasses = classNames({
    'account-card-album-custom-print-configurator-actions': true,
    'account-card-album-custom-print-configurator-actions-landscape':
      accountCard?.account_card_sides?.[0]?.card_side?.card_template
        ?.orientation === ORIENTATION.LANDSCAPE,
  });

  const accountCardClasses = classNames({
    'account-card-album-custom-print-configurator': true,
    'account-card-album-custom-print-configurator-landscape':
      accountCard?.account_card_sides?.[0]?.card_side?.card_template
        ?.orientation === ORIENTATION.LANDSCAPE,
  });

  return (
    <div className={classes}>
      <DesignEditor>
        <DesignEditorHeader>
          <DesignEditorHeaderLeftSlot>
            {onClose && (
              <Button
                btnStyle="link"
                className="text-bold"
                onClick={() => {
                  if (deleteAccountCardOnDismiss) {
                    onAccountCardDelete();
                  }
                  onClose();
                  onDismiss?.();
                }}
              >
                <Angle direction="left" />
              </Button>
            )}
          </DesignEditorHeaderLeftSlot>
          <DesignEditorHeaderCenterSlot>
            <h3 className="design-editor-header-title">
              {title ?? `Configure your ${name}`}
            </h3>
          </DesignEditorHeaderCenterSlot>

          <DesignEditorHeaderRightSlot>
            {!inlineButton && (
              <Button
                btnSize="sm"
                className="px-4 btn-round"
                btnOutline
                onClick={() => {
                  if (shouldAlertEmptyText({ accountCard })) {
                    openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
                      accountCard,
                      onElementConfigurationChange,
                      onComplete: () => {
                        onFinished({ accountCard });
                        closeModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL);
                      },
                      title:
                        "Heads up! You're print is missing the info below:",
                      buttonText: 'Save and finish',
                    });
                  } else {
                    onFinished({ accountCard });
                  }
                }}
                disabled={!accountCardConfigurationValidate(accountCard)}
              >
                {buttonText}
              </Button>
            )}
          </DesignEditorHeaderRightSlot>
        </DesignEditorHeader>
        <DesignEditorPreviewPane>
          <DesignEditorPreviewContent className="flex-column">
            {accountCard && presetFillStatus === PRESET_FILL_STATUS.FILLED ? (
              <Fragment>
                <h4 className="d-md-none text-center">
                  {title ?? `Configure your ${name}`}
                </h4>
                <div className={accountCardMainPhotoActionClasses}>
                  {photoElementReference && photoElements?.length === 1 && (
                    <Fragment>
                      {photoElementReference?.onPhotoChange && (
                        <Button
                          btnStyle="link"
                          btnSize="sm"
                          className="text-extra-bold"
                          onClick={() => {
                            photoElementReference.onPhotoChange();
                          }}
                        >
                          Change Photo
                        </Button>
                      )}
                      {photoElementReference?.onAdjustAndCrop && (
                        <Button
                          btnStyle="link"
                          btnSize="sm"
                          className="text-extra-bold"
                          onClick={() => {
                            photoElementReference.onAdjustAndCrop();
                          }}
                        >
                          Adjust &amp; Crop
                        </Button>
                      )}
                    </Fragment>
                  )}
                </div>
                <AccountCard
                  className={accountCardClasses}
                  onElementConfigurationChange={onElementConfigurationChange}
                  accountCard={accountCard}
                  cardElementLoading={cardElementLoading}
                  disablePhotoWatermarkOverlay={forceEntitlement}
                  photosContext={{
                    identity_id: allowMultiIdentityPhoto
                      ? undefined
                      : identity?.id,
                    album_id:
                      albumMembershipGroup &&
                      (albumMembershipGroup.is_album
                        ? albumMembershipGroup.id
                        : undefined),
                    album_folder_id:
                      albumMembershipGroup &&
                      (!albumMembershipGroup.is_album
                        ? albumMembershipGroup.id
                        : undefined),
                    root_album_folder_id:
                      albumMembershipGroup && albumMembershipGroup.is_album
                        ? albumMembershipGroup.album.root_album_folder?.id
                        : undefined,
                    is_album_membership_group: !!albumMembershipGroup?.identities,
                    is_all_photos: isAllPhotos,
                    force_entitlement: forceEntitlement,
                    uploads_disabled: !!isPictureDayMembership(
                      albumMembershipGroup,
                    ),
                  }}
                  watermarkSettings={watermarkSettings}
                  linkImageElements={linkImageElements}
                  onAddAccountCardElementReference={
                    onAddAccountCardElementReference
                  }
                />

                {hasPhotoElementsMissingConfiguration && (
                  <p className="text-italic text-center text-sm mt-2 mb-0">
                    {isMobile ? 'Tap' : 'Click'} on camera to choose your{' '}
                    {pluralize('photo', editablePhotoElements?.length ?? 0)}
                  </p>
                )}
                {!hasPhotoElementsMissingConfiguration &&
                  photoElements?.length > 1 && (
                    <p className="text-italic text-center text-sm mt-2 mb-0">
                      {isMobile ? 'Tap' : 'Click'} on a photo above to change
                      the photo or alter the crop.
                    </p>
                  )}
                {inlineButton && (
                  <Button
                    btnBlock
                    className="mt-2 btn-round inline-account-card-configurator-button"
                    btnOutline
                    onClick={() => {
                      if (shouldAlertEmptyText({ accountCard })) {
                        openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
                          accountCard,
                          onElementConfigurationChange,
                          onComplete: () => {
                            onFinished({ accountCard });
                          },
                        });
                      } else {
                        onFinished({ accountCard });
                      }
                    }}
                    disabled={!accountCardConfigurationValidate(accountCard)}
                  >
                    {buttonText}
                  </Button>
                )}
              </Fragment>
            ) : (
              <div className="account-card-album-custom-print-configurator d-flex">
                <Spinner className="m-auto" />
              </div>
            )}
          </DesignEditorPreviewContent>
        </DesignEditorPreviewPane>
      </DesignEditor>
      <AccountCardEmptyTextFormModal />
    </div>
  );
};

AccountCardAlbumCustomPrintConfigurator.propTypes = propTypes;

export default AccountCardAlbumCustomPrintConfigurator;
